import { CLIENT_SIZES } from 'constants/clientSizes';
import { isMail } from 'components/mail/scheduleSlots';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { COMPANY_DELETE_REASONS } from 'constants/companyDeleteReasons';
import { addDays, isoDay } from 'helper/timeLabels';
import { crmFieldsOf, draw, sizedCrm, thresholdsOf } from './crmCompanies';
import { companyHistory, companyResults } from './crmRecords';
import { LATENCY, ask, bigAccountsById, copy, dbOf, forget, later, meOf, read, realCompanies, realGroups, write } from './crmStore';

/**
 * The api of the record pages (a company, a group, a contact). Each read
 * gathers the real data of Jobbiz (the company, its members, its missions,
 * its invoices, its group) and adds the sales side the demo draws
 * (crmCompanies.js, crmRecords.js); each write goes to the demo's database.
 *
 * A group's record reads the api (POST admin/crm/group { groupId }): the
 * group, its companies { id, name, department, stage, missionCount,
 * lastExchangeAt }, its contacts { id, firstname, lastname, email, phone,
 * position } and `access: { canEditContacts }`. Its activity reads the api
 * too (POST admin/crm/group/activity { groupId } → { tasks, appointments,
 * calls, notes }), each entry filed under its company when it is one of the
 * group's, else under the group (groupActivitySources). Its missions and its invoices are still read company by company,
 * the members of its companies from the companies list. Its contacts are
 * written by crmApi.js (saveGroupContact, deleteGroupContact). A group gone
 * answers errorCode GROUP_NOT_FOUND. A note is written by the api too
 * (crmApi.js: addRecordNote, updateRecordNote, deleteRecordNote), and the
 * notes of a company's record and of a contact's are read from it (POST
 * admin/crm/notes { companyId } or { contactId }: the account's id, else
 * 'name:<name>'), so that a note linked to several records shows on each.
 * The position of a member with an account is the api's (memberPositions of
 * POST admin/crm/company/crm), read by a contact's page and a group's; the
 * company's page reads it with the rest of company/crm.
 *
 * The e-mails of a company's record, and of a group's and each of its
 * companies', are read from the api (POST admin/crm/record/emails { companyId }
 * | { groupId } → { data, intercom }): the sent ones and those Intercom holds
 * from the contacts; `history.intercom` is 'unavailable' when Intercom did not
 * answer. An e-mail is sent by crmApi.js (sendRecordEmail).
 *
 * The calls of a company's record, and those of a group's own contacts, are
 * Wildix's (POST admin/crm/record/calls, readPhoneCalls); a note may be
 * written on one (`phoneCallId`).
 *
 * Until the api has them:
 *
 *   getCompanyRecord     POST admin/crm/company        { companyId, canSeeInvoices }
 *   getGroupInvoiceTotals POST admin/crm/group/invoices { groupId }
 *   getContactRecord     POST admin/crm/contact        { companyId | groupId, contactId }
 *   logRecordCall        POST admin/crm/call           { companyId | groupId, contactName, result, note }
 *   cancelRecordEmail    POST admin/crm/email/cancel   { companyId | groupId, id }   a programmed e-mail not sent yet
 *   createRecordEvent    POST admin/crm/event          { companyId | groupId, title, startsAt, durationMin, mode, location, contactName, description }
 *   setCompanyStage      POST admin/crm/company/stage  { companyId, stage, lostReason, relaunchOn }
 *                        no longer called by the company's page: its moves go to the api
 *                        (moveCompanyToNew, markCompanyLost, reactivateProspect in crmApi.js)
 *   setCompanySize       POST admin/crm/company/size   { companyId, size: 'grand_compte' | 'pme' | 'tpe' | null }
 *                        the size chosen for a prospect; a client's follows its ETP (crm.etp, from its
 *                        timesheets) against the thresholds below, the chosen one until its first timesheet
 *   getClientSizeSettings  POST admin/crm/settings/client-sizes       { }  → { grand_compte, pme }
 *   saveClientSizeSettings POST admin/crm/settings/client-sizes/save  { grand_compte, pme }  (the ETP from
 *                        which a client is a key account, then an SME; below, a very small business)
 *   setLeadStatus        POST admin/crm/person/lead    { personId, status }
 *   listGroupCandidates  (the companies list, without those already in a group)  { search }
 *   addCompanyToGroup    POST admin/linkCompanyToBigAccount (real)  { idCompany, idBigAccount }
 *   makeCompanyGroup     POST admin/addBigAccount (real)  { name, idCompany, isLinked: true }, then the new group's id
 *   deleteCompany        POST admin/crm/company/delete  { companyId, reason, details }   a prospect only
 *   saveCompanyContact   POST admin/crm/company/contact/save    { companyId, contact }   (JBWEB-1424) a contact
 *                        without an account { id?, firstname, lastname, email, phone, position }: no id adds it
 *   deleteCompanyContact POST admin/crm/company/contact/delete  { companyId, contactId }
 *                        both on the real api in crmApi.js; the demo's stay for the ?mock= scenarios.
 *   setCompanyEmail      POST admin/crm/company/email   { companyId, email }   the company's general address
 *                        ('contact@…'), '' unsets it: on the real api in crmApi.js (read back as `crm.email`
 *                        of getCompanyCrm); the demo's stays for the ?mock= scenarios.
 *   setCompanyPhone      POST admin/crm/company/phone   { companyId, phone }   its switchboard (E.164; the api keeps
 *                        it in crmProspect.phone but does not answer it yet), '' unsets it; read back as `crm.phone`
 */

const REVIEWS_JOBS = ['Préparateur de commandes', 'Employé polyvalent', 'Agent d’entretien'];

const recordKey = ({ companyId, groupId }) => (groupId ? `group:${groupId}` : companyId);
const fullName = (person) => [person.firstname, person.lastname].filter(Boolean).join(' ');

// The positions of a company's members (their account's `user.position`), as
// admin/crm/company/crm answers them; a failed read leaves them empty.
const memberPositionsOf = (companyId) =>
  ask('getCrmCompanyCrm', { companyId: String(companyId) }).then(
    (answer) => answer?.memberPositions ?? {},
    () => ({}),
  );

// Its people: the members of the company (their account and their access),
// each with their position (`positions`, by account) and the lead status the
// demo gives them.
const membersOf = (company, db, positions = {}) =>
  (company?.employees ?? [])
    .filter((entry) => entry?.user?._id)
    .map((entry) => {
      const id = String(entry.user._id);
      return {
        id,
        firstname: entry.user.firstname ?? '',
        lastname: entry.user.lastname ?? '',
        email: entry.user.email ?? '',
        phone: entry.user.phone ?? '',
        role: entry.role,
        account: entry.user.validation === 0 ? 'pending' : 'active',
        position: positions[id] ?? '',
        leadStatus: db.leadStatus?.[id] ?? (draw({ _id: id }, 'lead', 6) === 0 ? 'ambassadeur' : ''),
      };
    });

// The contacts of HubSpot without a Jobbiz account: not registered yet.
const hubspotPeople = (contacts) =>
  contacts.map((contact) => ({
    id: `hs-${contact.hubspotId}`,
    hubspotId: contact.hubspotId,
    firstname: contact.firstname ?? '',
    lastname: contact.lastname ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    account: null,
    position: '',
    leadStatus: '',
  }));

// Its missions, to come, under way or over, with their temp worker.
const missionState = (mission, now) => {
  const start = new Date(mission.duration?.datestart);
  const end = new Date(mission.duration?.dateend ?? mission.duration?.datestart);
  end.setHours(23, 59, 59, 999);
  if (start > now) return 'upcoming';
  return end < now ? 'done' : 'current';
};
const missionsOf = ({ missions, tempWorkers }, now, companyName) => ({
  missions: missions.map((mission) => ({
    id: String(mission._id),
    number: mission.id_mission,
    title: mission.title,
    startsOn: mission.duration?.datestart,
    endsOn: mission.duration?.dateend ?? mission.duration?.datestart,
    state: missionState(mission, now),
    interrupted: Boolean(mission.interruption),
    worker: mission.tempWorker ? fullName(mission.tempWorker) : '',
    companyName,
  })),
  tempWorkers: tempWorkers.map((worker) => ({
    id: String(worker._id),
    name: fullName(worker),
    missions: worker.missionsCount ?? 0,
    positive: worker.ratings?.positive ?? 0,
    negative: worker.ratings?.negative ?? 0,
  })),
});

// What the client did in Jobbiz, drawn from its real missions: who created
// each one, and the reviews left on its temp workers.
const clientEventsOf = (companyId, { missions, tempWorkers }, members, now) => {
  const record = { _id: companyId };
  const actor = (salt) => (members.length ? fullName(members[draw(record, salt, members.length)]) : '');
  const created = missions.slice(0, 8).map((mission) => ({
    id: `${companyId}-created-${mission.id}`,
    kind: mission.interrupted ? 'mission_canceled' : 'mission_created',
    at: addDays(new Date(mission.startsOn), -(2 + draw(record, `created-${mission.id}`, 9))).toISOString(),
    actor: actor(`created-${mission.id}-by`),
    companyId,
    details: { number: mission.number, title: mission.title },
  }));
  const reviews = tempWorkers
    .filter((worker) => worker.positive || worker.negative)
    .slice(0, 4)
    .map((worker, index) => ({
      id: `${companyId}-review-${worker.id}`,
      kind: 'worker_review',
      at: addDays(now, -(8 + index * 17 + draw(record, `review-${worker.id}`, 9))).toISOString(),
      actor: actor(`review-${worker.id}-by`),
      companyId,
      details: {
        worker: worker.name,
        feedback: worker.negative > worker.positive ? 'negative' : 'positive',
        job: REVIEWS_JOBS[draw(record, `review-${worker.id}-job`, REVIEWS_JOBS.length)],
      },
    }));
  return [...created, ...reviews].filter((event) => event.actor);
};

// The demo knows the real company the way the tasks know theirs.
const ensureCompany = (db, company) => {
  if (db.companies.some((item) => item.id === company.id)) return;
  db.companies.push({ city: '', contactName: '', contactRole: '', phone: '', email: '', stage: 'prospect', ...company });
};

// A task shows on every record it is linked to: its company or group, every
// company, group and contact of its `links` (a contact on its company too).
export const tasksOfRecord = (tasks, { companyId, groupId }) =>
  tasks.filter((task) => {
    const links = task.links ?? [];
    if (groupId) return task.groupId === groupId || links.some((link) => link.type === 'group' && link.id === groupId);
    return (
      task.companyId === companyId ||
      links.some((link) => (link.type === 'company' && link.id === companyId) || (link.type === 'contact' && link.companyId === companyId))
    );
  });

// The history of a record: drawn once, then what the team adds.
const historyOf = (db, key, draft) => {
  db.records = db.records ?? {};
  if (!db.records[key]) db.records[key] = draft();
  return db.records[key];
};

// The invoices of the last 24 months, read year by year.
const invoiceTotals = async (companyId, now) => {
  const year = now.getFullYear();
  const years = await Promise.all([year, year - 1, year - 2].map((one) => ask('getCompanyInvoices', { idCompany: companyId, year: one }, `crm:invoices:${companyId}:${one}`).then((data) => data?.data?.items ?? [])));
  const items = years.flat().filter((invoice) => !['cancelled', 'draft', 'error', 'not_imported'].includes(invoice.status));
  const month = (offset) => isoDay(new Date(year, now.getMonth() - offset, 1)).slice(0, 7);
  const between = (from, to) => items.filter((invoice) => invoice.period >= month(from) && invoice.period <= month(to));
  const sum = (list, field) => list.reduce((total, invoice) => total + Number(invoice[field] || 0), 0);
  const unpaid = items.filter((invoice) => Number(invoice.remainingAmount) > 0);
  return {
    revenue: sum(between(11, 0), 'amountInclTax'),
    revenuePrev: sum(between(23, 12), 'amountInclTax'),
    unpaidAmount: sum(unpaid, 'remainingAmount'),
    unpaidCount: unpaid.length,
  };
};

const failOrRun = (payload, resolve, reject, run) => {
  if (payload?.scenario === 'chargement') return;
  if (payload?.scenario === 'erreur') {
    later(LATENCY.read, () => reject(new Error('Mock: the CRM api failed')));
    return;
  }
  run().then((value) => resolve(copy(value)), reject);
};

// A contact of a group, as the api answers it. A group has no account in
// Jobbiz; the lead status is still the demo's (setLeadStatus).
const groupContactOf = (db, contact) => ({
  id: String(contact.id),
  firstname: contact.firstname ?? '',
  lastname: contact.lastname ?? '',
  email: contact.email ?? '',
  phone: contact.phone ?? '',
  position: contact.position ?? '',
  account: null,
  leadStatus: db.leadStatus?.[String(contact.id)] ?? '',
});

const readGroup = (groupId) => ask('getCrmGroup', { groupId: String(groupId) });
const readGroupActivity = (groupId) => ask('getCrmGroupActivity', { groupId: String(groupId) });

// The notes linked to one record, in the shape buildActivity reads.
const readNotes = (key) =>
  ask('getCrmNotes', key).then((answer) => (answer?.notes ?? []).map((note) => ({ ...note, at: note.createdAt })));
// The e-mails of one record ({ companyId } or { groupId }), sent and received.
const readEmails = (key) =>
  ask('getCrmRecordEmails', key).then((answer) => ({ data: answer?.data ?? [], intercom: answer?.intercom ?? 'ok' }));
const authorsOf = (notes) => notes.map((note) => note.author).filter(Boolean);

/**
 * The Wildix calls of one record (POST admin/crm/record/calls { companyId } |
 * { groupId }), matched by its phone numbers. A failed read leaves the page
 * without them rather than without the page.
 */
const readPhoneCalls = (key) =>
  ask('getCrmRecordCalls', key)
    .then((answer) => (Array.isArray(answer?.data) ? answer.data : []))
    .catch(() => [])
    .then((calls) => calls.map((call) => ({ ...call, wildix: true })));
const latestFirst = (calls) => [...calls].sort((a, b) => new Date(b.at) - new Date(a.at));

// The companies an entry of the activity names: its own, then those of its links.
const linkedCompanies = (links) =>
  (links ?? []).map((link) => (link.type === 'company' ? link.id : link.type === 'contact' ? link.companyId : null));

/**
 * The activity of a group (POST admin/crm/group/activity) in the shape
 * buildActivity reads (activityItems.js), filed by record: a map from the
 * group's id or one of its companies' to { calls, events, tasks, notes }. An
 * entry goes to its company when it is one of the group's, else to the group;
 * a note written on a call goes where the call is, so that it shows under it,
 * once. A call carries `noteLink`, the record a note on it is linked to.
 * `people`: the authors and owners the api names, for their names.
 */
export const groupActivitySources = (activity, { group, companies }) => {
  const names = new Map(companies.map((company) => [company.id, company.name]));
  const bySource = new Map([group.id, ...companies.map((company) => company.id)].map((id) => [id, { calls: [], events: [], tasks: [], notes: [] }]));
  const sourceOf = (ids) => ids.find((id) => id && names.has(String(id))) ?? group.id;
  const people = [];
  const callSources = new Map();
  (activity?.calls ?? []).forEach((call) => {
    const source = sourceOf([call.companyId]);
    callSources.set(call.id, source);
    if (call.author) people.push(call.author);
    bySource.get(source).calls.push({
      id: call.id,
      at: call.at,
      authorId: call.author?.id ?? null,
      contactName: call.contactName ?? '',
      result: call.result,
      note: call.note ?? '',
      noteLink: source === group.id ? { type: 'group', id: group.id, label: group.name } : { type: 'company', id: source, label: names.get(source) },
    });
  });
  (activity?.appointments ?? []).forEach((appointment) => {
    if (appointment.owner) people.push(appointment.owner);
    bySource.get(sourceOf([appointment.companyId])).events.push({
      id: appointment.id,
      startsAt: appointment.startsAt,
      title: appointment.title ?? '',
      contactName: '',
      mode: '',
      outcome: '',
      description: '',
      status: appointment.status,
    });
  });
  (activity?.tasks ?? []).forEach((task) => {
    bySource.get(sourceOf([task.companyId, ...linkedCompanies(task.links)])).tasks.push(task);
  });
  (activity?.notes ?? []).forEach((note) => {
    if (note.author) people.push(note.author);
    const source = callSources.get(note.callId) ?? sourceOf(linkedCompanies(note.links));
    bySource.get(source).notes.push({ ...note, at: note.createdAt });
  });
  return { bySource, people };
};

// The people the page names, each once, the first list winning: the api's
// (the CRM's team, the authors and owners of the activity), then the demo's
// (its e-mails).
const peopleOf = (...lists) => {
  const seen = new Map();
  lists.flat().forEach((person) => person?.id && !seen.has(String(person.id)) && seen.set(String(person.id), { ...person, id: String(person.id) }));
  return [...seen.values()];
};

/**
 * Everything a company's page shows. `summary`: its line of the companies
 * list (its creation, its missions), which getCompanyInfo does not return.
 */
// `withPositions`: the members' positions are read here (a contact's page);
// the company's page reads them with the rest of company/crm.
const loadCompanyRecord = async (payload, { withInvoices = payload.canSeeInvoices, withPositions = false } = {}) => {
  const db = dbOf(payload);
  const me = meOf(payload);
  const now = new Date();
  const id = String(payload.companyId);
  const [info, missionData, list, groups, hubspot, totals, notes, positions, emails, phoneCalls, crmTeam] = await Promise.all([
    ask('getCompanyInfo', { idCompany: id }, `crm:info:${id}`).then((data) => data?.company ?? null),
    ask('getCompanyMissionsAndTempWorkers', { idCompany: id }, `crm:missions:${id}`).then((data) => ({
      missions: data?.missions ?? [],
      tempWorkers: data?.tempWorkers ?? [],
    })),
    realCompanies().catch(() => []),
    bigAccountsById().catch(() => ({})),
    ask('getCompanyHubspotContacts', { idCompany: id }, `crm:hubspot:${id}`).then((data) => data?.data ?? []).catch(() => []),
    withInvoices ? invoiceTotals(id, now).catch(() => null) : Promise.resolve(null),
    readNotes({ companyId: id }),
    withPositions ? memberPositionsOf(id) : Promise.resolve({}),
    readEmails({ companyId: id }).catch(() => ({ data: [], intercom: 'unavailable' })),
    readPhoneCalls({ companyId: id }),
    ask('getCrmTeam', {}).catch(() => []),
  ]);
  if (!info) throw new Error('Cette entreprise n’existe plus.');
  const name = info.name_commercial || info.reason_social || '';
  // Its calls are Wildix's, the demo's no longer shown; a note on one is linked to the company.
  const calls = latestFirst(phoneCalls).map((call) => ({ ...call, noteLink: { type: 'company', id, label: name } }));
  const summary = list.find((company) => String(company._id) === id) ?? null;
  const missions = missionsOf(missionData, now, name);
  const members = membersOf(info, db, positions);
  const crm = sizedCrm(
    {
      ...crmFieldsOf({ _id: id, missionCount: summary?.missionCount ?? missionData.missions.length, createdAt: summary?.createdAt }, { meId: me.id, team: db.team, now }),
      ...db.companyCrm?.[id],
    },
    db,
  );
  const contacts = members.map((member) => ({ name: fullName(member), email: member.email }));
  const history = historyOf(db, id, () =>
    companyHistory({ id, contacts, teamIds: db.team.map((person) => person.id), now, nextAction: crm.nextAction }),
  );
  ensureCompany(db, {
    id,
    name,
    city: info.address?.city ?? '',
    contactName: contacts[0]?.name ?? '',
    phone: members[0]?.phone ?? '',
    email: members[0]?.email ?? '',
    stage: crm.stage,
  });
  // Its next task, from the companies list, is a real task of the demo.
  if (crm.nextAction?.kind === 'task' && !history.nextTaskId) {
    const task = {
      id: `t-${id}`,
      type: 'todo',
      title: crm.nextAction.title,
      note: '',
      dueOn: crm.nextAction.dueOn,
      dueTime: null,
      durationMin: 30,
      doneAt: null,
      contactName: contacts[0]?.name ?? '',
      contactRole: '',
      contactDetail: '',
      companyId: id,
      assigneeId: crm.commercialId,
      callbackId: null,
      appointmentId: null,
      emailTemplate: '',
      emailSubject: '',
      emailBody: '',
      unread: false,
      createdAt: now.toISOString(),
      createdBy: crm.commercialId,
    };
    db.tasks.push(task);
    history.nextTaskId = task.id;
  }
  const tasks = tasksOfRecord(db.tasks, { companyId: id });
  const current = missions.missions.filter((mission) => mission.state === 'current');
  const groupContacts = groups[id]
    ? await readGroup(groups[id].id).then((answer) => (answer?.contacts ?? []).map((contact) => groupContactOf(db, contact)), () => [])
    : [];
  return {
    company: info,
    summary: summary && { createdAt: summary.createdAt, missionCount: summary.missionCount, department: summary.department },
    group: groups[id] ?? null,
    groupContacts,
    crm,
    members,
    hubspot: hubspotPeople(hubspot),
    missions,
    kpis: {
      current: current.length,
      toFill: current.filter((mission) => !mission.worker).length,
      invoices: totals,
    },
    history: {
      ...history,
      emails: emails.data,
      intercom: emails.intercom,
      calls,
      notes,
      tasks,
      clientEvents: clientEventsOf(id, missions, members, now),
    },
    results: companyResults({ id, history: { ...history, calls, notes, emails: emails.data }, stage: crm.stage, createdAt: summary?.createdAt, now }),
    team: peopleOf(authorsOf(notes), Array.isArray(crmTeam) ? crmTeam : [], db.team),
  };
};

const loadGroupRecord = async (payload) => {
  const db = dbOf(payload);
  const now = new Date();
  const [answer, activity, list, crmTeam, groupEmails, phoneCalls] = await Promise.all([
    readGroup(payload.groupId),
    readGroupActivity(payload.groupId),
    realCompanies().catch(() => []),
    ask('getCrmTeam', {}).catch(() => []),
    readEmails({ groupId: payload.groupId }).catch(() => ({ data: [], intercom: 'unavailable' })),
    readPhoneCalls({ groupId: String(payload.groupId) }),
  ]);
  const group = { id: String(answer.group.id), name: answer.group.name ?? '' };
  const groupLink = { type: 'group', id: group.id, label: group.name };
  const { bySource, people } = groupActivitySources(activity, {
    group,
    companies: (answer.companies ?? []).map((company) => ({ id: String(company.id), name: company.name ?? '' })),
  });
  const companies = await Promise.all(
    (answer.companies ?? []).map(async (company) => {
      const id = String(company.id);
      const name = company.name ?? '';
      // Its members: the companies list has them, the group's answer does not.
      const employees = list.find((one) => String(one._id) === id)?.employees ?? [];
      const [missionData, positions, emails] = await Promise.all([
        ask('getCompanyMissionsAndTempWorkers', { idCompany: id }, `crm:missions:${id}`)
          .then((data) => ({ missions: data?.missions ?? [], tempWorkers: data?.tempWorkers ?? [] }))
          .catch(() => ({ missions: [], tempWorkers: [] })),
        memberPositionsOf(id),
        // One company's failed read must not keep the whole group page from loading.
        readEmails({ companyId: id }).catch(() => ({ data: [], intercom: 'unavailable' })),
      ]);
      return {
        id,
        name,
        department: company.department ?? '',
        stage: company.stage ?? 'client',
        missionCount: company.missionCount ?? 0,
        lastExchangeAt: company.lastExchangeAt ?? null,
        members: employees.map((person) => ({
          id: String(person._id),
          firstname: person.firstname ?? '',
          lastname: person.lastname ?? '',
          email: person.email ?? '',
          phone: person.phone ?? '',
          account: 'active',
          position: positions[String(person._id)] ?? '',
        })),
        missions: missionsOf(missionData, now, name),
        history: { ...bySource.get(id), emails: emails.data, clientEvents: [] },
        intercom: emails.intercom,
      };
    }),
  );
  const contacts = (answer.contacts ?? []).map((contact) => groupContactOf(db, contact));
  // An e-mail one of its companies shows (the group's answer may hold its
  // companies' too) is not shown again under the group.
  const companyEmailIds = new Set(companies.flatMap((company) => company.history.emails.map((email) => email.id)));
  const ownEmails = groupEmails.data.filter((email) => !companyEmailIds.has(email.id));
  const intercom = [groupEmails, ...companies].some((one) => one.intercom === 'unavailable') ? 'unavailable' : 'ok';
  const current = companies.flatMap((company) => company.missions.missions.filter((mission) => mission.state === 'current'));
  return {
    group,
    access: { canEditContacts: Boolean(answer.access?.canEditContacts) },
    contacts,
    companies: companies.map(({ intercom: _intercom, ...company }) => company),
    // Its Wildix calls (its contacts' numbers) join the calls logged in the CRM.
    history: {
      ...bySource.get(group.id),
      calls: latestFirst([...bySource.get(group.id).calls, ...phoneCalls.map((call) => ({ ...call, noteLink: groupLink }))]),
      emails: ownEmails,
      intercom,
      clientEvents: [],
    },
    kpis: { current: current.length, toFill: current.filter((mission) => !mission.worker).length },
    team: peopleOf(Array.isArray(crmTeam) ? crmTeam : [], people, db.team),
  };
};

// The contacts without an account of a company, as the demo keeps them (the
// real ones come from admin/crm/company/crm, JBWEB-1424).
export const companyContactsOf = (payload) => dbOf(payload).companyContacts?.[String(payload.companyId)] ?? [];

const crmRecordApi = {
  getCompanyRecord: (payload, resolve, reject) => failOrRun(payload, resolve, reject, () => loadCompanyRecord(payload)),

  getGroupRecord: (payload, resolve, reject) => failOrRun(payload, resolve, reject, () => loadGroupRecord(payload)),

  // The invoices of every company of a group, added up: read after the page.
  getGroupInvoiceTotals: (payload, resolve, reject) =>
    failOrRun(payload, resolve, reject, async () => {
      const groups = await realGroups();
      const group = groups.find((one) => one.id === String(payload.groupId));
      const now = new Date();
      const totals = await Promise.all((group?.companyIds ?? []).map((id) => invoiceTotals(id, now).catch(() => null)));
      const known = totals.filter(Boolean);
      const sum = (field) => known.reduce((total, one) => total + one[field], 0);
      return known.length
        ? { revenue: sum('revenue'), revenuePrev: sum('revenuePrev'), unpaidAmount: sum('unpaidAmount'), unpaidCount: sum('unpaidCount') }
        : null;
    }),

  // A contact's page: the person, where they work, and their own history.
  getContactRecord: (payload, resolve, reject) =>
    failOrRun(payload, resolve, reject, async () => {
      const owner = payload.groupId
        ? await loadGroupRecord(payload)
        : await loadCompanyRecord(payload, { withInvoices: false, withPositions: true });
      const people = payload.groupId ? owner.contacts : [...owner.members, ...owner.hubspot];
      const person = people.find((one) => one.id === String(payload.contactId));
      if (!person) throw new Error('Ce contact n’est plus dans la fiche.');
      const name = fullName(person);
      // A member of a company has an account; a group's contact or a HubSpot one is known by name.
      const [list, notes] = await Promise.all([
        realCompanies().catch(() => []),
        readNotes({ contactId: !payload.groupId && person.account ? person.id : `name:${name}` }),
      ]);
      const ownerRef = payload.groupId
        ? { kind: 'group', id: owner.group.id, name: owner.group.name }
        : { kind: 'company', id: String(payload.companyId), name: owner.company.name_commercial || owner.company.reason_social };
      // Every company of whose members the person is (one account, several companies).
      const elsewhere = payload.groupId
        ? []
        : list
            .filter((company) => String(company._id) !== ownerRef.id && (company.employees ?? []).some((employee) => String(employee._id) === person.id))
            // A position is the person's: the same in each of their companies.
            .map((company) => ({ kind: 'company', id: String(company._id), name: company.name_commercial || company.reason_social, position: person.position }));
      const mine = (items, field = 'contactName') => (items ?? []).filter((item) => item[field] === name);
      return {
        person,
        owner: ownerRef,
        memberships: [{ ...ownerRef, position: person.position, role: person.role, account: person.account }, ...elsewhere],
        // A group's contact is edited by those its record allows.
        ...(payload.groupId && { access: owner.access }),
        history: {
          calls: mine(owner.history.calls),
          emails: (owner.history.emails ?? []).filter((email) => email.contactName === name || (person.email && (email.to?.length ? email.to : [email.address]).includes(person.email))),
          intercom: owner.history.intercom,
          notes,
          events: mine(owner.history.events),
          // Its first contact names the task; the others are in its links.
          tasks: (owner.history.tasks ?? []).filter(
            (task) => task.contactName === name || (task.links ?? []).some((link) => link.type === 'contact' && link.label === name),
          ),
          clientEvents: mine(owner.history.clientEvents, 'actor'),
        },
        team: peopleOf(authorsOf(notes), owner.team),
      };
    }),

  logRecordCall: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const history = historyOf(db, recordKey(payload), () => ({ calls: [], emails: [], notes: [], events: [] }));
      const call = { id: `c${Date.now()}`, at: new Date().toISOString(), authorId: me.id, contactName: payload.contactName ?? '', result: payload.result, note: payload.note?.trim() ?? '' };
      history.calls.push(call);
      return call;
    }),

  // The demo sends nothing: the e-mail is only written into the history. A
  // programmed one carries `scheduledAt` and its `at` is that time: once it is
  // past, the e-mail reads as sent.
  sendRecordEmail: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const history = historyOf(db, recordKey(payload), () => ({ calls: [], emails: [], notes: [], events: [] }));
      const scheduledAt = payload.scheduledAt && new Date(payload.scheduledAt) > new Date() ? payload.scheduledAt : null;
      const email = {
        id: `m${Date.now()}`,
        at: scheduledAt ?? new Date().toISOString(),
        scheduledAt,
        direction: 'sent',
        authorId: me.id,
        // The record, so that the activity can cancel a programmed e-mail.
        ...(payload.groupId ? { groupId: payload.groupId } : { companyId: payload.companyId }),
        contactName: payload.contactName ?? '',
        address: payload.address,
        to: payload.to ?? [payload.address],
        cc: payload.cc ?? [],
        bcc: payload.bcc ?? [],
        subject: payload.subject.trim(),
        body: payload.body.trim(),
        opens: [],
      };
      history.emails.push(email);
      return email;
    }),

  // A programmed e-mail that has not left yet goes away; one already sent stays.
  cancelRecordEmail: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const history = historyOf(db, recordKey(payload), () => ({ calls: [], emails: [], notes: [], events: [] }));
      const email = history.emails.find((one) => one.id === payload.id);
      if (!email || !email.scheduledAt || new Date(email.scheduledAt) <= new Date()) throw new Error('Cet e-mail est déjà parti.');
      history.emails = history.emails.filter((one) => one.id !== payload.id);
      return { id: payload.id };
    }),

  createRecordEvent: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const history = historyOf(db, recordKey(payload), () => ({ calls: [], emails: [], notes: [], events: [] }));
      const event = {
        id: `e${Date.now()}`,
        startsAt: payload.startsAt,
        durationMin: payload.durationMin,
        category: payload.category ?? 'rdv_client',
        title: payload.title?.trim() ?? '',
        contactName: payload.contactName ?? '',
        mode: payload.mode ?? '',
        location: payload.location ?? '',
        description: payload.description?.trim() ?? '',
        outcome: '',
        status: 'planned',
      };
      history.events.push(event);
      return event;
    }),

  setCompanyStage: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const today = isoDay(new Date());
      const change =
        payload.stage === 'lost'
          ? { stage: 'lost', lostAt: today, lostReason: payload.lostReason ?? '', relaunchOn: payload.relaunchOn ?? null, nextAction: null }
          : payload.stage === 'client'
            ? { stage: 'client', clientSince: today, lostAt: null, lostReason: null, relaunchOn: null }
            : { stage: 'prospect', lostAt: null, lostReason: null, relaunchOn: null };
      db.companyCrm = { ...db.companyCrm, [payload.companyId]: { ...db.companyCrm?.[payload.companyId], ...change } };
      return { companyId: payload.companyId, ...change };
    }),

  // The size of the client the sales plan counts (constants/clientSizes);
  // null unsets it.
  setCompanySize: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const size = payload.size ?? null;
      if (size !== null && !CLIENT_SIZES.includes(size)) throw new Error('Cette taille de client n’existe pas.');
      db.companyCrm = { ...db.companyCrm, [payload.companyId]: { ...db.companyCrm?.[payload.companyId], size } };
      return { companyId: payload.companyId, size };
    }),

  getClientSizeSettings: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db) => thresholdsOf(db)),

  saveClientSizeSettings: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const thresholds = { grand_compte: payload.grand_compte, pme: payload.pme };
      if (!Object.values(thresholds).every((etp) => typeof etp === 'number' && etp > 0))
        throw new Error('Un seuil est un nombre d’ETP supérieur à 0.');
      if (thresholds.pme >= thresholds.grand_compte)
        throw new Error('Une PME commence sous le seuil d’un grand compte.');
      db.clientSizeSettings = thresholds;
      return thresholds;
    }),

  setLeadStatus: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      db.leadStatus = { ...db.leadStatus, [payload.personId]: payload.status };
      return { personId: payload.personId, status: payload.status };
    }),

  // The companies that may join a group: those in none, found by name.
  listGroupCandidates: (payload, resolve, reject) =>
    failOrRun(payload, resolve, reject, async () => {
      const [list, groupOf] = await Promise.all([realCompanies(), bigAccountsById()]);
      const words = (payload.search ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      return list
        .filter((company) => !groupOf[String(company._id)])
        .map((company) => ({ id: String(company._id), name: company.name_commercial || company.reason_social || '', department: company.department ?? '' }))
        .filter((company) => !words || company.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(words))
        .slice(0, 30);
    }),

  // A real write: the company joins the group in Jobbiz.
  // A company made a group: the group takes its name and the company joins it
  // (the real addBigAccount), then the group is found again for its id.
  makeCompanyGroup: (payload, resolve, reject) =>
    ask('addBigAccount', { name: payload.name, idCompany: payload.companyId, isLinked: true })
      .then(() => {
        forget('groups');
        return bigAccountsById();
      })
      .then((groupOf) => resolve({ groupId: groupOf[payload.companyId]?.id ?? null }), reject),

  // A prospect deleted, and why: it leaves the list of the companies.
  deleteCompany: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const details = payload.details?.trim() ?? '';
      if (!COMPANY_DELETE_REASONS.includes(payload.reason)) throw new Error('Choisissez le motif de la suppression.');
      if (payload.reason === 'other' && !details) throw new Error('Précisez le motif de la suppression.');
      db.deletedCompanies = {
        ...db.deletedCompanies,
        [payload.companyId]: { reason: payload.reason, details, at: new Date().toISOString() },
      };
      return { companyId: payload.companyId };
    }),

  setCompanyEmail: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const email = (payload.email ?? '').trim().toLowerCase();
      if (email && !isMail(email)) throw new Error('Cette adresse e-mail n’est pas valide.');
      db.companyCrm = { ...db.companyCrm, [payload.companyId]: { ...db.companyCrm?.[payload.companyId], email } };
      return { companyId: payload.companyId, email };
    }),

  setCompanyPhone: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const phone = (payload.phone ?? '').trim();
      if (phone && !isValidPhoneNumber(phone)) throw new Error('Ce numéro n’est pas valide.');
      db.companyCrm = { ...db.companyCrm, [payload.companyId]: { ...db.companyCrm?.[payload.companyId], phone } };
      return { companyId: payload.companyId, phone };
    }),

  saveCompanyContact: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const companyId = String(payload.companyId);
      const { id, firstname = '', lastname = '', email = '', phone = '', position = '' } = payload.contact ?? {};
      const list = db.companyContacts?.[companyId] ?? [];
      if (id && !list.some((one) => one.id === id)) throw Object.assign(new Error('Ce contact n’existe plus.'), { response: { data: { errorCode: 'COMPANY_CONTACT_NOT_FOUND' } } });
      const contact = { id: id ?? `contact-${Date.now()}`, firstname, lastname, email, phone: phone ?? '', position };
      db.companyContacts = {
        ...db.companyContacts,
        [companyId]: id ? list.map((one) => (one.id === id ? contact : one)) : [...list, contact],
      };
      return { contact };
    }),

  deleteCompanyContact: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const companyId = String(payload.companyId);
      db.companyContacts = {
        ...db.companyContacts,
        [companyId]: (db.companyContacts?.[companyId] ?? []).filter((one) => one.id !== payload.contactId),
      };
      return { id: payload.contactId };
    }),

  addCompanyToGroup: (payload, resolve, reject) =>
    ask('linkCompanyToBigAccount', { idCompany: payload.companyId, idBigAccount: payload.groupId }).then((data) => {
      forget('groups');
      resolve(data);
    }, reject),
};

export default crmRecordApi;
